/**
 * Scan API 타입 및 Hooks
 * @MP/shared/api/hooks/scan에서 타입과 hooks를 모두 import할 수 있도록 함
 */

// 타입 re-export (실제 사용되는 타입만 명시적으로 export)
export type { RecFile } from '../generated/scan/model/rec-file';
export type { SessionInfo as ScanSessionInfo } from '../generated/scan/model/session-info';
export type { RecordingDuration } from '../generated/scan/model/recording-duration';
export { ScanSensorType } from '../generated/scan/model/scan-sensor-type';
export { ModuleControllerApi } from '../generated/scan/api/module-controller-api';

// Hooks 구현
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { compareDesc } from 'date-fns';

import SignalFileReader from '../../utils/signal/SignalFileReader';
import { toBlobResponse } from '../blobResponse';
import { API_CONFIG } from '../config/env';
import {
  ConfigControllerApi,
  EegQualityControllerApi,
  ModuleControllerApi,
  RecFileControllerApi,
  SensorControllerApi,
  SessionControllerApi,
} from '../generated/scan';
import { RecFile, Type } from '../generated/scan/model';
import { QK_MODULE_LIST, QK_REC_HISTORY, QK_SCAN_CONFIG, QK_SIGNAL_FILE } from '../query-keys';
import { ModuleStateChangeError } from '../types';

const baseURL = API_CONFIG.SCAN_URL;
const sessionControllerApi = new SessionControllerApi(undefined, baseURL);
const qualityControllerApi = new EegQualityControllerApi(undefined, baseURL);
const sensorControllerApi = new SensorControllerApi(undefined, baseURL);
const configControllerApi = new ConfigControllerApi(undefined, baseURL);
const moduleControllerApi = new ModuleControllerApi(undefined, baseURL);
const recFileControllerApi = new RecFileControllerApi(undefined, baseURL);

export const useFetchModuleSerials = () => {
  return useQuery({
    queryKey: [QK_MODULE_LIST],
    queryFn: () => moduleControllerApi.list().then((res) => res.data.map((v) => v.deviceSerial)),
  });
};

export const useLockSession = (type: Type) => {
  return useMutation<void, ModuleStateChangeError, { userId: string; deviceSerial: string }>({
    mutationFn: async ({ userId, deviceSerial }) => {
      try {
        await sessionControllerApi.createLock({ sensorType: type, deviceSerial: deviceSerial, subjectId: userId });
      } catch (e) {
        throw new ModuleStateChangeError(`Session Lock  실패`);
      }
    },
  });
};

export const useUnLockSession = () => {
  return useMutation<void, ModuleStateChangeError, { deviceSerial: string }>({
    mutationFn: async ({ deviceSerial }) => {
      try {
        await sessionControllerApi.deleteLock(deviceSerial);
      } catch (e) {
        throw new ModuleStateChangeError(`Session UnLock  실패`);
      }
    },
  });
};

export const useStartEEG_SQI = () => {
  return useMutation<void, ModuleStateChangeError, { deviceSerial: string }>({
    mutationFn: async ({ deviceSerial }) => {
      try {
        await qualityControllerApi.startSqi(deviceSerial);
      } catch (e) {
        throw new ModuleStateChangeError(`모듈 Start SQI 실패`);
      }
    },
  });
};

export const useStopEEG_SQI = () => {
  return useMutation<void, ModuleStateChangeError, { deviceSerial: string }>({
    mutationFn: async ({ deviceSerial }) => {
      try {
        await qualityControllerApi.stopSqi(deviceSerial);
      } catch (e) {
        throw new ModuleStateChangeError(`모듈 STOP SQI 실패`);
      }
    },
  });
};

export const useStartSignal = (type: Type) => {
  return useMutation<void, ModuleStateChangeError, { deviceSerial: string }>({
    mutationFn: async ({ deviceSerial }) => {
      try {
        await sensorControllerApi.startTransmission(deviceSerial, type);
      } catch (e) {
        throw new ModuleStateChangeError(`모듈 Start 실패`);
      }
    },
  });
};

export const useStopSignal = (type: Type) => {
  return useMutation<void, ModuleStateChangeError, { deviceSerial: string }>({
    mutationFn: async ({ deviceSerial }) => {
      try {
        await sensorControllerApi.stopTransmission(deviceSerial, type);
      } catch (e) {
        throw new ModuleStateChangeError(`모듈 STOP 실패`);
      }
    },
  });
};

export const useStartRecording = () => {
  return useMutation<void, ModuleStateChangeError, { deviceSerial: string }>({
    mutationFn: async ({ deviceSerial }) => {
      try {
        await sensorControllerApi.startRecording(deviceSerial);
      } catch (e) {
        throw new ModuleStateChangeError(`모듈 START RECORDING 실패`);
      }
    },
  });
};

export const useStopRecording = (type: Type) => {
  return useMutation<void, ModuleStateChangeError, { deviceSerial: string; save: boolean }>({
    mutationFn: async ({ deviceSerial, save }) => {
      try {
        await sensorControllerApi.stopRecording(deviceSerial, type, save);
      } catch (e) {
        throw new ModuleStateChangeError(`모듈 STOP RECORDING 실패`);
      }
    },
  });
};

export const useFetchSignalFile = (id: string = '') => {
  return useQuery({
    queryKey: [QK_SIGNAL_FILE, id],
    queryFn: async () => {
      if (id) {
        const res = await recFileControllerApi.downloadFile(id!!, { responseType: 'blob' });
        const blob = toBlobResponse(res.data);
        const buffer = await blob.arrayBuffer();
        return SignalFileReader.createWithFile(buffer).read();
      }
      return null;
    },
    gcTime: 0,
    placeholderData: () => undefined,
  });
};

export const useDownSignalFile = () => {
  return useMutation<any, AxiosError, { id: string; fileName: string }>({
    mutationFn: async ({ id, fileName }) => {
      const res = await recFileControllerApi.downloadFile(id, { responseType: 'blob' });
      const url = window.URL.createObjectURL(toBlobResponse(res.data));

      const a = document.createElement('a');
      a.style.display = 'none';
      a.href = url;
      a.download = `${fileName}.edf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
    },
  });
};

export const useFetchEdfFiles = (subjectId: string = '', type: Type, enabled: boolean = true) => {
  return useQuery<RecFile[]>({
    queryKey: [QK_REC_HISTORY, subjectId, type],
    queryFn: () => {
      return recFileControllerApi
        .searchFiles(subjectId, type)
        .then((res) =>
          res.data.sort((a, b) => compareDesc(new Date(b.recordingEndTime), new Date(a.recordingEndTime)))
        );
    },
    enabled: !!subjectId && enabled,
    gcTime: 0,
    placeholderData: () => undefined,
  });
};

export const useDeleteEdfFile = () => {
  const client = useQueryClient();
  return useMutation<any, AxiosError, { ids: string[]; subjectId: string; type: Type }>({
    mutationFn: ({ ids }) => recFileControllerApi.deleteFiles({ ids }),
    onSuccess: (_, { subjectId, type }) => client.invalidateQueries({ queryKey: [QK_REC_HISTORY, subjectId, type] }),
  });
};

export const useFetchScanSetting = () => {
  return useQuery({
    queryKey: [QK_SCAN_CONFIG],
    queryFn: () => configControllerApi.getConfig().then((res) => res.data),
    gcTime: 0,
  });
};
